import { useRef, useState } from 'react';

import { splitPrefillName } from '@/entities/client';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { useSheetStore } from '@/shared/stores/sheet-store';
import {
    SheetCloseButton,
    SheetDescription,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { AddClientForm } from './add-client/add-client-form';

// Mounted once per page that carries search, so it never remounts between opens. The form is keyed
// by a counter bumped on each open, which gives every open a fresh form.
export function AddClient() {
    const isOpen = useSheetStore((state) => state.currentSheet?.type === 'clients/add');
    const prefillName = useSheetStore((state) =>
        state.currentSheet?.type === 'clients/add'
            ? state.currentSheet.data?.prefillName
            : undefined,
    );
    const onCreated = useSheetStore((state) =>
        state.currentSheet?.type === 'clients/add' ? state.currentSheet.data?.onCreated : undefined,
    );
    const closeCurrentSheet = useSheetStore((state) => state.closeCurrentSheet);
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);

    const [wasOpen, setWasOpen] = useState(isOpen);
    const [openCount, setOpenCount] = useState(0);
    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) setOpenCount((count) => count + 1);
    }

    const hasUnsavedEntriesRef = useRef(false);

    const confirmDiscardChanges = (leave: () => void) => {
        if (!hasUnsavedEntriesRef.current) {
            leave();
            return;
        }

        openConfirmDialog({
            title: 'Discard unsaved changes?',
            confirmText: 'Discard',
            width: 'sm',
            onConfirm: leave,
        });
    };

    const handleClose = () => confirmDiscardChanges(() => closeCurrentSheet());

    return (
        <SheetPanel
            type="clients/add"
            side="right"
            size="medium"
            showCloseButton={false}
            onRequestClose={handleClose}
            onOpenAutoFocus={(event) => {
                // The close button is first in tab order, which would otherwise take focus.
                event.preventDefault();
                (event.currentTarget as HTMLElement).querySelector('input')?.focus();
            }}
        >
            <SheetHeader variant="mini" bordered>
                <SheetTitle>New Client</SheetTitle>
                <SheetDescription className="sr-only">
                    Create a new individual client.
                </SheetDescription>
                <SheetCloseButton onClick={handleClose} />
            </SheetHeader>
            <AddClientForm
                key={openCount}
                {...splitPrefillName(prefillName ?? '')}
                onCreated={onCreated}
                onCancel={handleClose}
                onUnsavedEntriesChange={(hasUnsavedEntries) => {
                    hasUnsavedEntriesRef.current = hasUnsavedEntries;
                }}
            />
        </SheetPanel>
    );
}
